import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import { Button } from '../ui/button';
import {
  CreateVendorInsurancePolicyForm,
  InsurancePolicyType,
  UpdateVendorInsurancePolicyForm,
  VendorInsurancePolicy,
} from '../../types';
import { INSURANCE_POLICY_TYPE_OPTIONS } from '../../utils/vendorCompliance';

interface InsurancePolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateVendorInsurancePolicyForm | UpdateVendorInsurancePolicyForm) => void;
  policy?: VendorInsurancePolicy | null;
  isLoading?: boolean;
}

const getDefaultForm = (policy?: VendorInsurancePolicy | null): CreateVendorInsurancePolicyForm & { isVerified: boolean } => ({
  policyType: policy?.policyType ?? InsurancePolicyType.GeneralLiability,
  customPolicyType: policy?.customPolicyType ?? '',
  carrierName: policy?.carrierName ?? '',
  policyNumber: policy?.policyNumber ?? '',
  coverageLimit: policy?.coverageLimit,
  effectiveDate: policy?.effectiveDate ? policy.effectiveDate.split('T')[0] : '',
  expirationDate: policy?.expirationDate ? policy.expirationDate.split('T')[0] : '',
  notes: policy?.notes ?? '',
  isVerified: policy?.isVerified ?? false,
});

const InsurancePolicyModal: React.FC<InsurancePolicyModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  policy,
  isLoading = false,
}) => {
  const [form, setForm] = useState(getDefaultForm(policy));

  useEffect(() => {
    if (isOpen) {
      setForm(getDefaultForm(policy));
    }
  }, [isOpen, policy]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const payload = {
      policyType: form.policyType,
      customPolicyType: form.policyType === InsurancePolicyType.Other ? form.customPolicyType : undefined,
      carrierName: form.carrierName || undefined,
      policyNumber: form.policyNumber || undefined,
      coverageLimit: form.coverageLimit,
      effectiveDate: form.effectiveDate || undefined,
      expirationDate: form.expirationDate || undefined,
      notes: form.notes || undefined,
      ...(policy ? { isVerified: form.isVerified } : {}),
    };

    onSubmit(payload as CreateVendorInsurancePolicyForm | UpdateVendorInsurancePolicyForm);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={policy ? 'Edit Insurance Policy' : 'Add Insurance Policy'}
      maxWidth="2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Policy Type
          </label>
          <select
            value={form.policyType}
            onChange={(e) => setForm((prev) => ({ ...prev, policyType: Number(e.target.value) as InsurancePolicyType }))}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            required
          >
            {INSURANCE_POLICY_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {form.policyType === InsurancePolicyType.Other && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Custom Policy Type
            </label>
            <input
              type="text"
              value={form.customPolicyType}
              onChange={(e) => setForm((prev) => ({ ...prev, customPolicyType: e.target.value }))}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              required
            />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Carrier
            </label>
            <input
              type="text"
              value={form.carrierName}
              onChange={(e) => setForm((prev) => ({ ...prev, carrierName: e.target.value }))}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Policy Number
            </label>
            <input
              type="text"
              value={form.policyNumber}
              onChange={(e) => setForm((prev) => ({ ...prev, policyNumber: e.target.value }))}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Coverage Limit
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.coverageLimit ?? ''}
              onChange={(e) => setForm((prev) => ({
                ...prev,
                coverageLimit: e.target.value ? Number(e.target.value) : undefined,
              }))}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Effective Date
            </label>
            <input
              type="date"
              value={form.effectiveDate}
              onChange={(e) => setForm((prev) => ({ ...prev, effectiveDate: e.target.value }))}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Expiration Date
            </label>
            <input
              type="date"
              value={form.expirationDate}
              onChange={(e) => setForm((prev) => ({ ...prev, expirationDate: e.target.value }))}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Notes
          </label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            rows={3}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
          />
        </div>

        {policy && (
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={form.isVerified}
              onChange={(e) => setForm((prev) => ({ ...prev, isVerified: e.target.checked }))}
              className="rounded border-gray-300"
            />
            Mark policy as verified
          </label>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : policy ? 'Update Policy' : 'Add Policy'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default InsurancePolicyModal;
